<script setup lang="ts">
import { useAuthStore } from '../stores/auth.store';
import { startRollout } from '../stores/rollouts';
/**
 * One Publish drawer for every entry point (legacy CODOT had five, each a little different — publishing doc
 * anti-pattern #6). Two directions:
 *  - content → targets: card ⋮ / Information Panel / bulk bar / campaign Save & Publish. The header names
 *    what is being published (legacy never did, P1); Players, Player Groups and Tags are picked together in
 *    one searchable list (legacy: exclusive tabs, P3) with a live reach count; "Publish on all Players".
 *  - target → content: "Publish New Content" on a player or a group; items already there are marked (P7).
 * Placement: end of each list (legacy) or start (P5). Success is confirmed with a toast (P2).
 */
import { useI18n } from 'vue-i18n';
import type { ContentRef, GroupsHealth, ScreenGroup, ScreenSummary } from '@codot/contracts';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { ACCEPTED_UPLOAD_TYPES } from '@codot/contracts';
import { startUploads, uploads, uploadsVersion } from '../stores/uploads';
import { quietUploads, startPublishIntent } from '../stores/publish-intent';
import { groupsApi, mediaApi } from '../api/content.api';
import { errorMessage } from '../api/http';
import { publishingApi } from '../api/publishing.api';
import { allPlayers as playersOf, groupsHealth, playersReach, screensByIds } from '../api/screens.api';
import { wontPlay } from '../utils/plays';
import { toast } from '../stores/toast';
import PlayerPicker from './pickers/PlayerPicker.vue';
import ContentPicker, { type PickItem, type PickServer } from './pickers/ContentPicker.vue';
import SlideOverDrawer from './SlideOverDrawer.vue';

export interface PublishItem {
  ref: ContentRef;
  name: string;
  type: 'image' | 'video' | 'app' | 'campaign' | 'playlist';
  thumbnailUrl?: string | null;
}
/** `ids`: several players at once (Players list › select › Publish); `id` is then the first and `name` a summary. */
export type PublishTarget = { kind: 'player' | 'group'; id: string; name: string; ids?: string[] };

const props = defineProps<{
  open: boolean;
  /** content → targets */
  content?: PublishItem[];
  /** target → content ("Publish New Content") */
  target?: PublishTarget | null;
  /** Content ids already in the target's list (target mode). */
  existing?: string[];
  /** Content keys (`kind:id`) ticked when it opens (target mode). */
  preselect?: string[];
  /** Uploads already started for this target (e.g. the empty player's Upload button): followed and ticked here. */
  uploadKeys?: string[];
}>();
const emit = defineEmits<{ close: []; published: [] }>();

const loading = ref(false);
const busy = ref(false);
const error = ref<string | null>(null);
const groups = ref<ScreenGroup[]>([]);
const allTags = ref<string[]>([]);
/** Every player this member may publish to (the "all players" count). */
const fleetSize = ref(0);
/** Per group: players online / offline / not connected (from the server, not the whole fleet). */
const groupHealth = ref(new Map<string, GroupsHealth[string]>());
/** Players that already have the (single) item directly — marked in the list. */
const alreadyOn = ref<Set<string>>(new Set());

const query = ref('');
const pickPlayers = ref<string[]>([]);
const pickGroups = ref<string[]>([]);
const pickTags = ref<string[]>([]);
const allPlayers = ref(false);
/** This member works only with some player groups. */
const limitedToGroups = computed(() => !!useAuthStore().activeOrganization?.groupIds);
const placement = ref<'end' | 'start'>('end');
const newTag = ref('');
const pickContent = ref<string[]>([]); // `${kind}:${id}`

const isTargetMode = computed(() => !!props.target);

/**
 * What the target already has (content id → how many of the targeted players have it), so each card can say so.
 * Callers that know it pass `existing`; otherwise it's looked up here (one player, several, or a group).
 */
const alreadyThere = ref(new Map<string, number>());
function alreadyNote(id: string): string | null {
  const n = alreadyThere.value.get(id) ?? 0;
  if (!n) return null;
  if (targetCount.value > 1) return n >= targetCount.value ? t('publish.onAllTargets') : t('publish.onNOfM', { n, m: targetCount.value });
  return props.target?.kind === 'group' ? t('publish.alreadyInGroup') : t('publish.alreadyOnPlayer');
}
const targetCount = computed(() => (props.target?.kind === 'player' ? (props.target.ids?.length ?? 1) : 1));
/** The players content goes to (target mode): each card says if some of them can't show it. */
const targetPlayers = ref<ScreenSummary[]>([]);
async function loadTargetPlayers(target: PublishTarget): Promise<void> {
  targetPlayers.value = [];
  try {
    targetPlayers.value = target.kind === 'player' ? await screensByIds(target.ids ?? [target.id]) : await playersOf({ group: target.id });
  } catch {
    /* no flags then: the publish still warns */
  }
}
async function loadAlreadyThere(target: PublishTarget): Promise<void> {
  alreadyThere.value = new Map();
  if (props.existing) return void (alreadyThere.value = new Map(props.existing.map((id) => [id, targetCount.value])));
  try {
    const ids = target.kind === 'player' ? (target.ids ?? [target.id]) : [target.id];
    const lists = await Promise.all(
      ids.map((id) => (target.kind === 'player' ? publishingApi.playerContent(id) : publishingApi.groupContent(id)).then((c) => c.items.map((i) => i.content.id))),
    );
    const out = new Map<string, number>();
    for (const list of lists) for (const id of new Set(list)) out.set(id, (out.get(id) ?? 0) + 1);
    alreadyThere.value = out;
  } catch {
    /* the cards just don't say it */
  }
}
const title = computed(() => {
  if (props.target) return t('publish.titleTarget', { name: props.target.name });
  const c = props.content ?? [];
  return c.length === 1 ? t('publish.titleOne', { name: c[0]!.name }) : t('publish.titleMany', { n: c.length });
});

watch(
  () => props.open,
  async (open) => {
    if (!open) return;
    error.value = null;
    query.value = '';
    pickPlayers.value = [];
    pickGroups.value = [];
    pickTags.value = [];
    pickContent.value = [...(props.preselect ?? [])];
    myUploads.value = [...(props.uploadKeys ?? [])];
    newMenu.value = false;
    allPlayers.value = false;
    placement.value = 'end';
    loading.value = true;
    try {
      if (props.target) {
        // The content picker pages the library from the server itself.
        void loadAlreadyThere(props.target);
        void loadTargetPlayers(props.target);
      } else {
        const one = props.content?.length === 1 ? props.content[0]!.ref : null;
        const [all, health, g, tg, u] = await Promise.all([
          playersReach({ allPlayers: true }),
          groupsHealth().catch(() => ({})),
          groupsApi.list(),
          publishingApi.tags().catch(() => []),
          one ? publishingApi.usage(one).catch(() => null) : Promise.resolve(null),
        ]);
        fleetSize.value = all.players;
        groupHealth.value = new Map(Object.entries(health));
        groups.value = g;
        allTags.value = tg;
        alreadyOn.value = new Set((u?.players ?? []).filter((p) => p.via.type === 'direct').map((p) => p.id));
      }
    } catch (err) {
      error.value = errorMessage(err);
    } finally {
      loading.value = false;
    }
  },
  { immediate: true },
);

/* ---------------- content → targets ---------------- */
const q = computed(() => query.value.trim().toLowerCase());
const visibleGroups = computed(() => groups.value.filter((g) => !q.value || g.name.toLowerCase().includes(q.value)));
const tagChoices = computed(() => {
  const all = [...new Set([...allTags.value, ...pickTags.value])];
  return all.filter((tg) => !q.value || tg.toLowerCase().includes(q.value));
});
/** Every player this publish reaches (players ∪ group members ∪ tag holders), counted by the server. */
const reach = ref(0);
let reachTimer: ReturnType<typeof setTimeout> | undefined;
let reachSeq = 0;
watch([pickPlayers, pickGroups, pickTags, allPlayers], () => {
  clearTimeout(reachTimer);
  if (allPlayers.value) return void (reach.value = fleetSize.value);
  if (!pickPlayers.value.length && !pickGroups.value.length && !pickTags.value.length) return void (reach.value = 0);
  const seq = ++reachSeq;
  reachTimer = setTimeout(async () => {
    const r = await playersReach({ players: pickPlayers.value, groups: pickGroups.value, tags: pickTags.value }).catch(() => null);
    if (r && seq === reachSeq) reach.value = r.players;
  }, 150);
});
onBeforeUnmount(() => clearTimeout(reachTimer));
const LISTS = { players: pickPlayers, groups: pickGroups, tags: pickTags, content: pickContent } as const;
/** Toggle a value in one of the pick lists (by name: refs are unwrapped inside templates). */
function toggle(list: keyof typeof LISTS, value: string): void {
  const r = LISTS[list];
  r.value = r.value.includes(value) ? r.value.filter((v) => v !== value) : [...r.value, value];
}

const { t } = useI18n();
function addTag(): void {
  const tg = newTag.value.trim();
  if (tg && !pickTags.value.some((x) => x.toLowerCase() === tg.toLowerCase())) pickTags.value = [...pickTags.value, tg];
  newTag.value = '';
}

/* ---------------- target → content ---------------- */
const keyOf = (r: ContentRef) => `${r.kind}:${r.id}`;

/* ---------------- target → content: make something new right here ---------------- */
const route = useRoute();
const router = useRouter();
const newMenu = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);
const ACCEPT = [...ACCEPTED_UPLOAD_TYPES, '.mov', '.mkv'].join(',');
/** Upload entries started from this drawer (keys in the app-wide uploads store). */
const myUploads = ref<string[]>([]);
const mine = computed(() => uploads.filter((u) => myUploads.value.includes(u.key)));
function pickFiles(): void {
  newMenu.value = false;
  fileInput.value?.click();
}
function onFiles(e: Event): void {
  const input = e.target as HTMLInputElement;
  const files = Array.from(input.files ?? []);
  input.value = '';
  if (!files.length) return;
  // Into the library's top level, like any upload; progress also shows in the uploads tray.
  startUploads(files, null);
  const keys = uploads.slice(0, files.length).map((u) => u.key);
  keys.forEach((k) => quietUploads.add(k));
  myUploads.value = [...myUploads.value, ...keys];
}
// As each of ours reaches the server it joins the list, ticked; READY ones come back with the library reload.
const ticked = new Set<string>();
watch(uploadsVersion, async () => {
  if (!props.open || !mine.value.length) return;
  for (const u of mine.value)
    if (u.asset && !ticked.has(u.asset.id)) {
      ticked.add(u.asset.id);
      pickContent.value = [...pickContent.value, `media:${u.asset.id}`];
    }
  const ready = mine.value.filter((u) => u.stage === 'ready' && u.asset && !readyIds.value.has(u.asset.id)).map((u) => u.asset!.id);
  if (ready.length) {
    const found = await mediaApi.byIds(ready).catch(() => []);
    readyIds.value = new Set([...readyIds.value, ...found.filter((a) => a.status === 'READY').map((a) => a.id)]);
  }
});
/** Our uploads the server says are ready to play. */
const readyIds = ref(new Set<string>());
/** Ours that are on the server but not playable yet (optimizing): shown, ticked, and they hold Publish back. */
const notReady = computed(() =>
  mine.value.filter((u) => u.asset && u.stage !== 'error' && u.stage !== 'cancelled' && !readyIds.value.has(u.asset.id)),
);
const waiting = computed(
  () => mine.value.some((u) => u.stage === 'uploading') || notReady.value.some((u) => pickContent.value.includes(`media:${u.asset!.id}`)),
);
type NewKind = 'composition-new' | 'playlist-new' | 'campaign-new' | 'web_page' | 'app';
function createNew(kind: NewKind): void {
  if (!props.target) return;
  newMenu.value = false;
  // Web page and apps are made in Content's New App panel (`?newApp=`); the rest have their own editors.
  const app = kind === 'web_page' || kind === 'app';
  startPublishIntent(props.target, route.fullPath, app ? 'dashboard' : kind);
  emit('close');
  void router.push(app ? { name: 'dashboard', query: { newApp: kind === 'app' ? 'pick' : kind } } : { name: kind });
}
/** The content picker pages the library from the server; our uploads still optimizing come first. */
const pickServer = computed<PickServer>(() => ({
  playable: true,
  pinned: notReady.value.map((u) => ({
    key: `media:${u.asset!.id}`,
    name: u.asset!.name,
    type: (u.asset!.kind === 'video' ? 'video' : 'image') as PickItem['type'],
    thumbnailUrl: u.asset!.thumbnailUrl ?? null,
    note: t('publish.optimizing', { p: u.optimize != null ? `${u.optimize}%` : '' }),
  })),
  note: (key) => alreadyNote(key.slice(key.indexOf(':') + 1)),
  wontPlay: (asset) => (targetPlayers.value.length ? wontPlay(asset, targetPlayers.value) : null),
}));
const TYPE_BADGE: Record<PublishItem['type'], string> = {
  app: 'bg-pink-100 text-pink-800 dark:bg-pink-950 dark:text-pink-300',
  campaign: 'bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300',
  playlist: 'bg-teal-100 text-teal-800 dark:bg-teal-950 dark:text-teal-300',
  video: 'bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300',
  image: 'bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-300',
};

const canPublish = computed(
  () =>
    !busy.value &&
    !loading.value &&
    (isTargetMode.value
      ? pickContent.value.length > 0 && !waiting.value
      : allPlayers.value || reach.value > 0 || pickTags.value.length > 0),
);

async function publish(): Promise<void> {
  if (!canPublish.value) return;
  busy.value = true;
  error.value = null;
  try {
    const content = isTargetMode.value
      ? pickContent.value.map((k) => {
          const [kind, id] = k.split(':') as [ContentRef['kind'], string];
          return { kind, id };
        })
      : (props.content ?? []).map((c) => c.ref);
    const res = await publishingApi.publish({
      content,
      players: props.target?.kind === 'player' ? (props.target.ids ?? [props.target.id]) : allPlayers.value ? [] : pickPlayers.value,
      groups: props.target?.kind === 'group' ? [props.target.id] : allPlayers.value ? [] : pickGroups.value,
      tags: isTargetMode.value || allPlayers.value ? [] : pickTags.value,
      allPlayers: !isTargetMode.value && allPlayers.value,
      placement: placement.value,
    });
    const skipped = res.skippedDuplicates ? t('publish.skipped', { n: res.skippedDuplicates }) : '';
    // "Published to …" comes with the sync tray (below): when every player is offline, its notice says it instead.
    const confirm = res.placementsCreated ? t('publish.publishedTo', { players: t('publish.nPlayers', res.playersReached), skipped }) : null;
    if (!confirm) toast(t('publish.nothingNew', { skipped }), { tone: 'info' });
    // Published, but worth knowing (e.g. a campaign plays more videos at once than a Samsung display can).
    // Content too big for a player: that player is skipped; offer to take this publish off it (each one by name).
    const tooBig = (res.warnings ?? []).filter((w) => w.code === 'CONTENT_TOO_BIG' && w.screenId);
    for (const w of tooBig) {
      toast(`${w.screenName}: ${w.message}`, {
        tone: 'warning',
        ms: 30_000,
        action: { label: t('publish.leaveOut'), run: () => void leaveOut(content, w.screenId!, w.screenName) },
      });
    }
    const other = (res.warnings ?? []).filter((w) => !tooBig.includes(w));
    if (other.length)
      toast(
        `${other[0]!.screenName}: ${other[0]!.message}${other.length > 1 ? ` ${t('publish.moreWarnings', { n: other.length - 1 }, other.length - 1)}` : ''}`,
        { tone: 'warning', ms: 20_000 },
      );
    // Follow the players it changed until each one plays the new content (sync tray).
    const items = props.content ?? [];
    startRollout(
      props.target
        ? t('sync.labelTarget', { name: props.target.name })
        : items.length === 1
          ? items[0]!.name
          : t('sync.labelMany', { n: items.length }),
      res,
      confirm ? { message: confirm } : undefined,
    );
    emit('published');
    emit('close');
  } catch (err) {
    error.value = errorMessage(err);
  } finally {
    busy.value = false;
  }
}

/** Take the content just published off one player (its own copy only: a group or tag keeps it for the others). */
async function leaveOut(content: ContentRef[], screenId: string, name: string): Promise<void> {
  try {
    for (const c of content) {
      const u = await publishingApi.usage(c);
      for (const p of u.players.filter((x) => x.id === screenId)) await publishingApi.unpublish(p.placementId);
    }
    toast(t('publish.leftOut', { name }), { tone: 'success' });
    // The drawer is closed by now: lists showing "published on N players" refresh themselves.
    window.dispatchEvent(new CustomEvent('codot:publications-changed'));
  } catch (err) {
    toast(errorMessage(err), { tone: 'error' });
  }
}
</script>

<template>
  <SlideOverDrawer :open="open" size="lg" :title="title" :persistent="busy" @close="emit('close')">
    <template v-if="!isTargetMode && content?.length" #header-extra>
      <div class="mt-2 flex flex-wrap gap-1.5">
        <span v-for="c in content.slice(0, 6)" :key="keyOf(c.ref)" class="badge" :class="TYPE_BADGE[c.type]">{{
          c.name
        }}</span>
        <span v-if="content.length > 6" class="badge bg-slate-100 dark:bg-slate-800"
          >{{ t('publish.more', { n: content.length - 6 }) }}</span
        >
      </div>
    </template>

    <div class="space-y-5">
      <input
        v-model="query"
        type="search"
        class="input"
        :placeholder="isTargetMode ? t('publish.searchContent') : t('publish.searchTargets')"
        :aria-label="t('publish.search')"
      />
      <p v-if="loading" class="text-sm text-slate-500">{{ t('publish.loading') }}</p>

      <!-- content → targets -->
      <template v-else-if="!isTargetMode">
        <!-- Access limited by group: no "every player", no tags (the server refuses them too). -->
        <label
          v-if="!limitedToGroups"
          class="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-2.5 text-sm dark:border-slate-800"
        >
          <input v-model="allPlayers" type="checkbox" class="h-4 w-4 accent-indigo-600" />
          <span class="font-medium">{{ t('publish.allPlayers') }}</span>
          <span class="ms-auto text-xs text-slate-500"
            >{{ t('publish.nPlayers', fleetSize) }}</span
          >
        </label>

        <fieldset :disabled="allPlayers" class="space-y-5" :class="allPlayers ? 'opacity-50' : ''">
          <PlayerPicker
            v-model="pickPlayers"
            server
            :query="query"
            :note="(s) => (alreadyOn.has(s.id) ? t('publish.alreadyOnPlayer') : null)"
          />

          <section>
            <h3 class="mb-1.5 flex items-center text-xs font-medium tracking-wider text-slate-500 uppercase">
              {{ t('publish.groups') }} <span class="ms-auto normal-case">{{ t('publish.nSelected', { n: pickGroups.length }) }}</span>
            </h3>
            <ul
              class="max-h-40 divide-y divide-slate-100 overflow-y-auto rounded-md border border-slate-200 text-sm dark:divide-slate-800 dark:border-slate-800"
            >
              <li v-for="g in visibleGroups" :key="g.id">
                <label
                  class="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/50"
                >
                  <input
                    type="checkbox"
                    class="h-4 w-4 accent-indigo-600"
                    :checked="pickGroups.includes(g.id)"
                    @change="toggle('groups', g.id)"
                  />
                  <span class="min-w-0 flex-1 truncate">{{ g.name }}</span>
                  <span class="flex shrink-0 items-center gap-2 text-xs text-slate-500">
                    <span v-if="groupHealth.get(g.id)?.online" class="inline-flex items-center gap-1" :title="t('players.stOnline')"
                      ><span class="h-2 w-2 rounded-full bg-emerald-500" aria-hidden="true" />{{ t('publish.nOnline', { n: groupHealth.get(g.id)!.online }) }}</span
                    >
                    <span v-if="groupHealth.get(g.id)?.offline" class="inline-flex items-center gap-1" :title="t('players.stOffline')"
                      ><span class="h-2 w-2 rounded-full bg-red-500" aria-hidden="true" />{{ t('publish.nOffline', { n: groupHealth.get(g.id)!.offline }) }}</span
                    >
                    <span>{{ t('publish.nPlayers', g.screenCount) }}</span>
                  </span>
                </label>
              </li>
              <li v-if="!visibleGroups.length" class="px-3 py-2 text-slate-500">{{ t('publish.noGroups') }}</li>
            </ul>
          </section>

          <section v-if="!limitedToGroups">
            <h3 class="mb-1.5 text-xs font-medium tracking-wider text-slate-500 uppercase">{{ t('publish.tags') }}</h3>
            <div class="flex flex-wrap gap-1.5">
              <button
                v-for="tg in tagChoices"
                :key="tg"
                type="button"
                class="rounded-full border px-2.5 py-0.5 text-xs"
                :class="
                  pickTags.includes(tg)
                    ? 'border-indigo-500 bg-indigo-50 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-200'
                    : 'border-slate-300 hover:border-slate-400 dark:border-slate-700'
                "
                :aria-pressed="pickTags.includes(tg)"
                @click="toggle('tags', tg)"
              >
                {{ tg }}
              </button>
              <form class="flex" @submit.prevent="addTag">
                <input
                  v-model="newTag"
                  class="input w-36 py-0.5 text-xs"
                  maxlength="40"
                  :placeholder="t('publish.addTag')"
                  :aria-label="t('publish.addTagSr')"
                />
              </form>
            </div>
            <p class="mt-1 text-xs text-slate-500">
              {{ t('publish.tagsHint') }}
            </p>
          </section>
        </fieldset>
      </template>

      <!-- target → content -->
      <template v-else>
        <!-- Nothing suitable yet? Make it from here: uploads land in this list ticked; editors come back here. -->
        <div class="flex flex-wrap items-center gap-2" data-menu>
          <div class="relative">
            <button type="button" class="btn-secondary px-2.5 py-1 text-sm" :aria-expanded="newMenu" aria-haspopup="menu" data-testid="publish-new" @click="newMenu = !newMenu">
              + {{ t('publish.newContent') }}
            </button>
            <div
              v-if="newMenu"
              role="menu"
              class="absolute start-0 z-30 mt-1 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 text-sm shadow-lg dark:border-slate-700 dark:bg-slate-900"
            >
              <button role="menuitem" type="button" class="block w-full px-3 py-2 text-start hover:bg-slate-50 dark:hover:bg-slate-800" @click="pickFiles">
                <span class="block font-medium">{{ t('publish.newUpload') }}</span>
                <span class="block text-xs text-slate-500">{{ t('publish.newUploadHint') }}</span>
              </button>
              <div class="my-1 border-t border-slate-100 dark:border-slate-800" />
              <button
                v-for="opt in ([['web_page', 'newWebPage'], ['app', 'newApp'], ['composition-new', 'newComposition'], ['playlist-new', 'newPlaylist'], ['campaign-new', 'newCampaign']] as const)"
                :key="opt[0]"
                role="menuitem"
                type="button"
                class="block w-full px-3 py-2 text-start hover:bg-slate-50 dark:hover:bg-slate-800"
                @click="createNew(opt[0])"
              >
                <span class="block font-medium">{{ t(`publish.${opt[1]}`) }}</span>
                <span class="block text-xs text-slate-500">{{ t('publish.newEditorHint') }}</span>
              </button>
            </div>
          </div>
          <input ref="fileInput" type="file" multiple class="hidden" :accept="ACCEPT" @change="onFiles" />
          <span v-if="mine.some((u) => u.stage === 'uploading')" class="text-xs text-slate-500" aria-live="polite">
            {{ t('publish.uploadingN', { n: mine.filter((u) => u.stage === 'uploading').length }) }}
          </span>
        </div>
        <p v-for="u in mine.filter((x) => x.stage === 'error')" :key="u.key" class="alert-error text-xs" role="alert">
          {{ u.name }} · {{ t('publish.uploadFailed', { error: u.error }) }}
        </p>
        <ContentPicker v-model="pickContent" :server="pickServer" :query="query" />
      </template>

      <fieldset class="space-y-1.5 text-sm">
        <legend class="label">{{ t('publish.where') }}</legend>
        <!-- Two cards: where the new items go in the player's list, with a tiny picture of it. -->
        <div class="grid grid-cols-2 gap-2">
          <label
            v-for="opt in (['end', 'start'] as const)"
            :key="opt"
            class="flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors has-focus-visible:ring-2 has-focus-visible:ring-indigo-500"
            :class="placement === opt ? 'border-indigo-500 bg-indigo-50/60 dark:bg-indigo-950/40' : 'border-slate-200 hover:border-slate-300 dark:border-slate-700'"
          >
            <input v-model="placement" type="radio" :value="opt" class="sr-only" />
            <span class="flex w-7 shrink-0 flex-col gap-0.5" aria-hidden="true">
              <span v-for="i in 4" :key="i" class="h-1 rounded-full" :class="(opt === 'start' ? i === 1 : i === 4) ? 'bg-indigo-500' : 'bg-slate-300 dark:bg-slate-600'" />
            </span>
            <span class="min-w-0">
              <span class="block font-medium" :class="placement === opt ? 'text-indigo-800 dark:text-indigo-200' : ''">{{ opt === 'end' ? t('publish.atEnd') : t('publish.atStart') }}</span>
              <span class="block text-xs text-slate-500">{{ opt === 'end' ? t('publish.atEndHint') : t('publish.atStartHint') }}</span>
            </span>
          </label>
        </div>
      </fieldset>
      <p v-if="error" class="alert-error" role="alert">{{ error }}</p>
    </div>

    <template #footer>
      <span v-if="!isTargetMode" class="me-auto self-center text-sm text-slate-500" aria-live="polite">
        {{ t('publish.reach', { players: t('publish.nPlayers', reach) }) }}<template v-if="pickTags.length">{{ t('publish.plusFuture') }}</template>
      </span>
      <span v-else class="me-auto self-center text-sm text-slate-500" aria-live="polite">{{ waiting ? t('publish.waitUploads') : t('publish.nSelected', { n: pickContent.length }) }}</span>
      <button type="button" class="btn-secondary" :disabled="busy" @click="emit('close')">{{ t('publish.cancel') }}</button>
      <button type="button" class="btn-primary" :disabled="!canPublish" @click="publish">
        {{ busy ? t('publish.publishing') : t('publish.publish') }}
      </button>
    </template>
  </SlideOverDrawer>
</template>
